
import { supabase } from '@/integrations/supabase/client';

/**
 * Creates an audit record for certificate lifecycle events
 */
export async function createCertificateAuditRecord({
  certificateNumber,
  inventoryId,
  actionType,
  status,
  details,
  adminId
}: {
  certificateNumber: string;
  inventoryId?: string;
  actionType: 'created' | 'verified' | 'reported' | 'revoked' | 'updated' | 'viewed';
  status?: string;
  details?: any;
  adminId?: string;
}): Promise<{ success: boolean; error: any }> {
  try {
    // Insert the audit record
    const { error } = await supabase
      .from('certificate_verification_history')
      .insert({
        certificate_number: certificateNumber,
        inventory_id: inventoryId,
        verification_status: status || 'unknown',
        verified_by: adminId || (await supabase.auth.getUser()).data?.user?.id,
        notes: typeof details === 'string' 
          ? details 
          : JSON.stringify(details || {}),
      });
      
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error creating certificate audit record:", error);
    return { success: false, error };
  }
}

/**
 * Retrieves audit history for a specific certificate
 */
export async function getCertificateAuditHistory(
  certificateNumber: string,
  limit: number = 50
): Promise<{ data: any[]; error: any }> {
  try {
    const { data, error } = await supabase
      .from('certificate_verification_history')
      .select(`
        id,
        certificate_number,
        verification_status,
        verification_date,
        verified_by,
        notes,
        admin_users (full_name, email)
      `)
      .eq('certificate_number', certificateNumber)
      .order('verification_date', { ascending: false })
      .limit(limit);
      
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error("Error retrieving certificate audit history:", error);
    return { data: [], error };
  }
}

/**
 * Updates a certificate and creates an audit record
 */
export async function updateCertificateWithAudit({
  certificateId,
  certificateNumber,
  status,
  details,
  adminId,
  actionType = 'updated'
}: {
  certificateId: string;
  certificateNumber: string;
  status: string;
  details?: any;
  adminId?: string;
  actionType?: 'verified' | 'reported' | 'revoked' | 'updated';
}): Promise<{ success: boolean; error: any }> {
  try {
    // First update the certificate status
    const { error: updateError } = await supabase
      .from('certificate_number_inventory')
      .update({
        verification_status: status,
        verification_date: new Date().toISOString(),
        verified_by: adminId || (await supabase.auth.getUser()).data?.user?.id
      })
      .eq('id', certificateId);
      
    if (updateError) throw updateError;
    
    // Then create the audit record
    const { success, error: auditError } = await createCertificateAuditRecord({
      certificateNumber,
      inventoryId: certificateId,
      actionType,
      status,
      details,
      adminId
    });
    
    if (auditError) throw auditError;
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error updating certificate with audit:", error);
    return { success: false, error };
  }
}
